import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DriverBankTransactionReasonConstants } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Driver, DriverBank, DriverBankTransaction, DriverBankTransactionReason, DriverBankTransactionType } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class DriverBankService extends UzurvRestService {
  readonly resourceUrl = 'driverBanks/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Retrieves a driver's bank by their ID
   *
   * @param driverId
   * @permission driver_bank_read
   */
  getDriverBankByDriverId(driverId: number): Observable<DriverBank> {
    return this.get(`${this.resourceUrl}drivers/${driverId}/driverBank`).pipe(map(response => Deserialize(response, DriverBank)));
  }

  /**
   * @param driverId
   * @param startDate
   * @param endDate
   * @permission DRIVER_BANK_READ
   */
  listDriverBankTransactionsByDriverId(
    driverId: number,
    startDate: Date,
    endDate: Date,
    pageNumber?: number,
    pageSize?: number
  ): Observable<Array<DriverBankTransaction>> {
    const urlStartDate = moment(startDate).format('YYYY-MM-DD');
    const urlEndDate = moment(endDate).format('YYYY-MM-DD');
    return this.get(`${this.resourceUrl}drivers/${driverId}/driverBankTransactions/`, {
      startDate: urlStartDate,
      endDate: urlEndDate,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, DriverBankTransaction)));
  }

  /**
   * Returns a matching transaction reason for the given name
   *
   * @param transactionReasonName
   */
  findDriverBankTransactionReasonByName(transactionReasonName: string): Observable<DriverBankTransactionReason> {
    return this.get(`${this.resourceUrl}driverBankTransactionReasons/`, {
      name: transactionReasonName
    }).pipe(map(result => Deserialize(result, DriverBankTransactionReason)));
  }

  /**
   * Lists all driver bank transaction reasons
   *
   * @param pageNumber
   * @param pageSize
   * @permission driver_bank_transaction_reason_read
   */
  listDriverBankTransactionReasons(pageNumber: number, pageSize: number): Observable<Array<DriverBankTransactionReason>> {
    return this.get(`${this.resourceUrl}driverBankTransactionReasons/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, DriverBankTransactionReason)));
  }

  /**
   * Lists the driver bank transaction credit reasons by filtering debit reasons from the
   * REST method response
   */
  listDriverBankTransactionCreditReasons(): Observable<Array<DriverBankTransactionReason>> {
    return this.listDriverBankTransactionReasons(0, 100).pipe(
      map(response =>
        response.filter(reason => !DriverBankTransactionReasonConstants.DRIVER_BANK_TRANSACTION_DEBIT_REASONS.includes(reason.name))
      )
    );
  }

  /**
   * Lists the driver bank transaction credit reasons by filtering debit reasons from the
   * REST method response
   */
  listDriverBankTransactionDebitReasons(): Observable<Array<DriverBankTransactionReason>> {
    return this.listDriverBankTransactionReasons(0, 100).pipe(
      map(response =>
        response.filter(reason => !DriverBankTransactionReasonConstants.DRIVER_BANK_TRANSACTION_CREDIT_REASONS.includes(reason.name))
      )
    );
  }

  /**
   * Returns a matching transaction type for the given name
   *
   * @param transactionTypeName
   */
  findDriverBankTransactionTypeByName(transactionTypeName: string): Observable<DriverBankTransactionType> {
    return this.get(`${this.resourceUrl}driverBankTransactionTypes/`, {
      name: transactionTypeName
    }).pipe(map(response => Deserialize(response, DriverBankTransactionType)));
  }

  /**
   * Creates a new bank transaction for the given driver
   *
   * @param driverBankTransaction
   * @param driver
   * @permission driver_bank_transaction_create
   */
  createDriverBankTransactionForDriver(driverBankTransaction: DriverBankTransaction, driver: Driver): Observable<DriverBankTransaction> {
    return this.post(`driverBanks/${driver.id}/driverBankTransactions/`, Serialize(driverBankTransaction)).pipe(
      map(response => Deserialize(response, DriverBankTransaction))
    );
  }
}
